/** Safe presentation formatters. UI must never expose JavaScript's "Invalid Date". */
export const isValidDateValue = (value: unknown): boolean => {
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  if (typeof value !== 'string' && typeof value !== 'number') return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime());
};

export const formatDate = (value: unknown, fallback = 'Date not available'): string => {
  if (!isValidDateValue(value)) return fallback;
  const date = value instanceof Date ? value : new Date(value as string | number);
  return date.toLocaleDateString();
};

export const formatTime = (value: unknown, fallback = 'Time not available'): string => {
  if (!isValidDateValue(value)) return fallback;
  const date = value instanceof Date ? value : new Date(value as string | number);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const formatDateTime = (value: unknown, fallback = 'Date not available'): string => {
  if (!isValidDateValue(value)) return fallback;
  const date = value instanceof Date ? value : new Date(value as string | number);
  return date.toLocaleString();
};

/**
 * Numeric boundary helpers.
 *
 * A production crash (doctor ReferralModal: `TypeError: s.toFixed is not a
 * function`) happened because the API can legitimately return a numeric-looking
 * value as a STRING: MySQL DECIMAL columns and driver/JSON serialisation do not
 * guarantee JavaScript `number`. Calling `.toFixed` on `"4.8"` throws, React
 * unmounts the subtree, and the global ErrorBoundary swallows the whole modal.
 *
 * The fix belongs at the BOUNDARY, not at the one expression that happened to
 * throw, so every consumer of an API number goes through here.
 *
 * Contract:
 *   number (finite)  -> accepted
 *   numeric string   -> converted safely
 *   null/undefined   -> unavailable (null)
 *   NaN/Infinity     -> unavailable (null)
 *   malformed string -> unavailable (null)
 *   object/array/bool-> unavailable (null)
 *
 * IMPORTANT: never coerce malformed input to 0. Zero is a real clinical and
 * financial value (a genuinely free consultation, a zero balance) and using it
 * as an error sentinel would silently corrupt meaning.
 */
export const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    // Reject empty strings, whitespace and anything non-numeric. `Number('')` is
    // 0 and `Number(' ')` is 0, both of which would be false data.
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * True when a value is a usable, strictly-POSITIVE rating.
 *
 * Guards like `rating > 0` do NOT protect a `.toFixed()` call: `"4.8" > 0` is
 * true (loose comparison coerces) while `"4.8".toFixed(1)` throws. Use this
 * before rendering any numeric formatting on API data.
 */
export const hasPositiveRating = (value: unknown): boolean => {
  const rating = toFiniteNumber(value);
  return rating !== null && rating > 0;
};

/** A one-decimal rating (e.g. 4.8) or the localised "unavailable" label. */
export const formatRating = (
  value: unknown,
  unavailable = 'N/A',
  digits = 1
): string => {
  const rating = toFiniteNumber(value);
  return rating === null ? unavailable : rating.toFixed(digits);
};

/**
 * A currency amount or the "unavailable" label.
 * A genuine 0 renders as 0 — only unparseable input falls back.
 */
export const formatCurrency = (
  value: unknown,
  unavailable = 'N/A',
  locale = 'en-TZ',
  currency = 'TZS'
): string => {
  const amount = toFiniteNumber(value);
  if (amount === null) return unavailable;
  try {
    return amount.toLocaleString(locale, { style: 'currency', currency, maximumFractionDigits: 0 });
  } catch {
    // An unsupported locale/currency in a given runtime must not blank the UI.
    return amount.toLocaleString();
  }
};

/**
 * Canonical MONEY amount: grouped, exactly two decimals, NO currency symbol.
 *
 * Use this where the currency is already part of the markup or of a translated
 * sentence ("{{cost}} TZS") — otherwise the prefix would appear twice.
 *
 * Contract (shared with `formatMoney`):
 *   number / numeric string   -> "200.00"
 *   genuine 0                 -> "0.00"     (zero is real money)
 *   null/undefined/malformed  -> the `unavailable` label — never "0.00",
 *                                because a MISSING amount is not a zero amount
 *
 * The locale is pinned deliberately: a device locale that writes decimal commas
 * must not render "1.250,50" on one screen and "1,250.50" on another.
 */
export const formatAmount = (value: unknown, unavailable = '—'): string => {
  const amount = toFiniteNumber(value);
  if (amount === null) return unavailable;
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Canonical MONEY text: `formatAmount` with the currency code in front.
 *
 * Why this exists next to `formatCurrency` (Intl currency style, zero fraction
 * digits, used by a legacy screen): this is the shape every money surface in
 * BHABY actually renders ("TZS 200.00"), and it must be produced in ONE place.
 *
 *   number / numeric string   -> "TZS 200.00"
 *   genuine 0                 -> "TZS 0.00"
 *   null/undefined/malformed  -> the `unavailable` label
 *
 * Apply it ONCE, at the display boundary, to a value that is still raw. Never
 * feed its output back into arithmetic.
 */
export const formatMoney = (
  value: unknown,
  unavailable = '—',
  currency = 'TZS'
): string => {
  const amount = formatAmount(value, unavailable);
  return amount === unavailable ? unavailable : `${currency} ${amount}`;
};

/**
 * Sum RAW money values numerically.
 *
 * Production defect this closes (patient Payment History, Sep 2026): MySQL
 * DECIMAL columns reach the browser as STRINGS, so
 *
 *     payments.reduce((sum, p) => sum + (p.amount || 0), 0)
 *
 * did not add anything — `0 + "200.00"` is `"0200.00"`, and the next iteration
 * produced `"0200.00200.00"`. The screen rendered
 * `TZS 0200.00200.00200.00200.00` instead of `TZS 600.00`. TypeScript could not
 * catch it because the local interface declared `amount: number` while the API
 * sends a string.
 *
 * Every value goes through `toFiniteNumber` first, so a formatted string, an
 * empty value or a malformed row contributes 0 instead of poisoning the total
 * with concatenated text. Summing is the caller's job; formatting is not.
 */
export const sumBy = <T,>(items: T[], pick: (item: T) => unknown): number =>
  items.reduce((sum, item) => sum + (toFiniteNumber(pick(item)) ?? 0), 0);

/** Array guard for API list fields that may be null/object/undefined. */
export const toArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

/**
 * Normalise an API LIST payload to an array.
 *
 * Production defect this closes (found by the async-modal harness): a
 * *successful* response carrying a non-array in its envelope — e.g.
 * `{ success: true, data: { unexpected: 'shape' } }` — reached state and the
 * next `.map()` threw inside render. React unmounted the subtree and the global
 * ErrorBoundary swallowed the whole modal.
 *
 * The idiom used across the app,
 *     Array.isArray(x) ? x : x?.data || []
 * looks defensive but returns the bad object unchanged whenever `data` is a
 * non-array, so it does not actually protect the render.
 *
 * Accepts both envelope shapes the backend uses and is total:
 *     [...]                  -> [...];
 *     { data: [...] }        -> [...];
 *     { data: {...} }        -> [];
 *     null / undefined / {}  -> [].
 *
 * The element type defaults to `any` so a call site keeps the permissiveness the
 * expression it replaces had (it yielded `any[]`); callers that want a concrete
 * element type can pass it explicitly, e.g. `toArrayPayload<Booking>(res)`.
 */
export const toArrayPayload = <T = any,>(payload: unknown): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object') {
    const nested = (payload as { data?: unknown }).data;
    if (Array.isArray(nested)) return nested as T[];
  }
  return [];
};
